import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchInventoryDashboardData } from '../../../store/actions/inventoryDashboardActions'
import Loader from '../../../components/elements/Loader'
import { Link } from 'react-router-dom'
import ArrowIcon from '../../../components/elements/icons/ArrowIcon'
import EmptyState from '../../../components/elements/EmptyState'

const InventoryDashboard = () => {
  const dispatch = useDispatch()
  const inventoryDashboardSelector = useSelector((state) => state.inventoryDashboard)

  useEffect(() => {
    dispatch(fetchInventoryDashboardData())
  }, [dispatch])

  if (inventoryDashboardSelector.loadingInventory) {
    return <Loader />
  }

  const dashboardData = inventoryDashboardSelector.inventoryData || {}
  const overview = dashboardData.overview || {}
  const stock = dashboardData.stock || {}
  const itemsByCategory = dashboardData.itemsByCategory || []
  const variantsByCondition = dashboardData.variantsByCondition || []
  const assignments = dashboardData.assignments || {}
  const stockMovements = dashboardData.stockMovements || []

  return (
    <div className='w-full'>
      {/* Header */}
      <div className='flex items-center justify-between mb-6'>
        <div>
          {/* <h2 className='text-2xl font-bold text-gray-800'>Inventory Dashboard</h2> */}
        </div>
        <Link to="/admin/inventory/inventory-items" className='px-4 py-3 bg-vcm-purple text-white rounded-lg hover:bg-purple-700 transition duration-200 text-sm flex items-center gap-x-2'>
          Manage Inventory <ArrowIcon className="w-4 h-4" />
        </Link>
      </div>

      {/* Overview Stats Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-2'>
        {/* Total Items */}
        <div className='bg-vcm-purple rounded-lg p-6 shadow-lg'>
          <p className='text-xs uppercase tracking-[0.2em] text-white text-opacity-80 mb-2'>Total Items</p>
          <h2 className='text-4xl font-bold text-white font-space-grotesk'>{overview.totalItems || 0}</h2>
          <div className='mt-3 pt-3 border-t border-white border-opacity-20'>
            <div className='flex items-center justify-between text-xs text-white text-opacity-80'>
              <span>Categories:</span>
              <span className='font-semibold'>{overview.totalCategories || 0}</span>
            </div>
          </div>
        </div>

        {/* Total Variants */}
        <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
          <p className='text-xs uppercase tracking-wider text-gray-500 mb-2'>Total Variants</p>
          <h2 className='text-4xl font-bold text-vcm-purple font-space-grotesk'>{overview.totalVariants || 0}</h2>
          <p className='text-xs text-gray-600 mt-2'>
            {stock.assignedVariants || 0} assigned
          </p>
        </div>

        {/* Inventory Value */}
        <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
          <p className='text-xs uppercase tracking-wider text-gray-500 mb-2'>Total Value</p>
          <h2 className='text-2xl font-bold text-vcm-purple font-space-grotesk'>
            ₦{((overview.totalInventoryValue || 0) / 100)?.toLocaleString()}
          </h2>
          <p className='text-xs text-gray-600 mt-2'>
            Avg: ₦{((overview.averageItemValue || 0) / 100)?.toLocaleString()}
          </p>
        </div>

        {/* Assignments */}
        <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
          <p className='text-xs uppercase tracking-wider text-gray-500 mb-2'>Total Assignments</p>
          <h2 className='text-4xl font-bold text-vcm-purple font-space-grotesk'>{overview.totalAssignments || 0}</h2>
          <p className='text-xs text-green-600 mt-2'>
            +{assignments.recent || 0} recent
          </p>
        </div>
      </div>

      {/* Stock Status Section */}
      <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200 mb-8'>
        <h3 className='text-lg font-medium text-gray-800 mb-4'>Stock Status</h3>
        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4'>
          <div className='text-center p-4 bg-blue-50 rounded-lg'>
            <p className='text-xs text-gray-600 mb-1'>Total Stock</p>
            <p className='text-3xl font-bold text-blue-600 font-space-grotesk'>{stock.totalStock || 0}</p>
          </div>
          <div className='text-center p-4 bg-green-50 rounded-lg'>
            <p className='text-xs text-gray-600 mb-1'>Avg per Item</p>
            <p className='text-3xl font-bold text-green-600 font-space-grotesk'>{(stock.averageStock || 0).toFixed(1)}</p>
          </div>
          <div className='text-center p-4 bg-orange-50 rounded-lg'>
            <p className='text-xs text-gray-600 mb-1'>Low Stock</p>
            <p className='text-3xl font-bold text-orange-600 font-space-grotesk'>{stock.lowStockItems || 0}</p>
          </div>
          <div className='text-center p-4 bg-red-50 rounded-lg'>
            <p className='text-xs text-gray-600 mb-1'>Out of Stock</p>
            <p className='text-3xl font-bold text-red-600 font-space-grotesk'>{stock.outOfStockItems || 0}</p>
          </div>
          <div className='text-center p-4 bg-purple-50 rounded-lg'>
            <p className='text-xs text-gray-600 mb-1'>Assigned</p>
            <p className='text-3xl font-bold text-purple-600 font-space-grotesk'>{stock.assignedVariants || 0}</p>
          </div>
          <div className='text-center p-4 bg-yellow-50 rounded-lg'>
            <p className='text-xs text-gray-600 mb-1'>Perishable</p>
            <p className='text-3xl font-bold text-yellow-600 font-space-grotesk'>{stock.perishableItems || 0}</p>
          </div>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8'>
        {/* Items by Category */}
        <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
          <h3 className='text-lg font-medium text-gray-800 mb-4'>Items by Category</h3>
          {itemsByCategory.length > 0 ? (
            <div className='space-y-3'>
              {itemsByCategory.map((category, index) => (
                <div key={index} className='border-b border-gray-100 pb-3 last:border-0'>
                  <div className='flex items-center justify-between mb-2'>
                    <span className='text-sm font-medium text-gray-800'>{category.categoryName}</span>
                    <span className='text-lg font-bold text-vcm-purple font-space-grotesk'>
                      {category.count}
                    </span>
                  </div>
                  <div className='flex items-center justify-between text-xs text-gray-600'>
                    <span className='text-green-600'>Deductible: {category.deductible || 0}</span>
                    <span className='text-orange-600'>Non-Deductible: {category.nonDeductible || 0}</span>
                  </div>
                  {/* Progress bar */}
                  <div className='w-full bg-gray-200 rounded-full h-2 mt-2'>
                    <div 
                      className='h-2 rounded-full bg-vcm-purple'
                      style={{width: `${overview.totalItems > 0 ? (category.count / overview.totalItems) * 100 : 0}%`}}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState emptyStateText="No category data available" />
          )}
        </div>

        {/* Variants by Condition */}
        <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
          <h3 className='text-lg font-medium text-gray-800 mb-4'>Variants by Condition</h3>
          {variantsByCondition.length > 0 ? (
            <div className='space-y-4'>
              {variantsByCondition.map((variant, index) => (
                <div key={index} className='p-4 bg-gray-50 rounded-lg'>
                  <div className='flex items-center justify-between mb-2'>
                    <span className='text-sm font-medium text-gray-700 capitalize'>{variant._id}</span>
                    <span className='text-2xl font-bold text-vcm-purple font-space-grotesk'>
                      {variant.count}
                    </span>
                  </div>
                  <div className='flex items-center justify-between'>
                    <span className='text-xs text-gray-600'>Total Value:</span>
                    <span className='text-sm font-semibold text-green-600 font-space-grotesk'>
                      ₦{((variant.totalValue || 0) / 100)?.toLocaleString()}
                    </span>
                  </div>
                  <div className='w-full bg-gray-200 rounded-full h-2 mt-3'>
                    <div 
                      className={`h-2 rounded-full ${
                        variant._id === 'new' ? 'bg-green-500' : 
                        variant._id === 'used' ? 'bg-orange-500' : 
                        'bg-blue-500'
                      }`}
                      style={{width: `${overview.totalVariants > 0 ? (variant.count / overview.totalVariants) * 100 : 0}%`}}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState emptyStateText="No variant data available" />
          )}

          {/* Deductible vs Non-Deductible */}
          <div className='mt-6 pt-4 border-t border-gray-200'>
            <h4 className='text-sm font-medium text-gray-700 mb-3'>Item Classification</h4>
            <div className='grid grid-cols-2 gap-3'>
              <div className='text-center p-3 bg-green-50 rounded-lg'>
                <p className='text-xs text-gray-600 mb-1'>Deductible</p>
                <p className='text-2xl font-bold text-green-600 font-space-grotesk'>
                  {overview.deductibleItems}
                </p>
              </div>
              <div className='text-center p-3 bg-orange-50 rounded-lg'>
                <p className='text-xs text-gray-600 mb-1'>Non-Deductible</p>
                <p className='text-2xl font-bold text-orange-600 font-space-grotesk'>
                  {overview.nonDeductibleItems}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Assignments by Department */}
        <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
          <h3 className='text-lg font-medium text-gray-800 mb-4'>Assignments by Department</h3>
          {assignments?.byDepartment?.length > 0 ? (
            <div className='space-y-3'>
              {assignments.byDepartment.map((dept, index) => (
                <div key={index} className='flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition duration-200'>
                  <div className='flex items-center gap-x-3'>
                    <div className='w-10 h-10 rounded-full bg-vcm-purple text-white flex items-center justify-center text-sm font-bold'>
                      {dept.count}
                    </div>
                    <span className='text-sm font-medium text-gray-800'>{dept.departmentName}</span>
                  </div>
                  <div className='text-right'>
                    <p className='text-xs text-gray-500'>
                      {assignments.total > 0 ? ((dept.count / assignments.total) * 100).toFixed(1) : 0}%
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState emptyStateText="No assignment data available" />
          )}

          {/* Stock Movements */}
          <div className='mt-6 pt-4 border-t border-gray-200'>
            <h4 className='text-sm font-medium text-gray-700 mb-3'>Stock Movements</h4>
            <div className='space-y-3'>
              {stockMovements.map((movement, index) => (
                <div key={index} className='flex items-center justify-between p-3 rounded-lg' style={{
                  backgroundColor: movement._id === 'increment' ? 'rgb(240, 253, 244)' : 'rgb(254, 242, 242)'
                }}>
                  <div>
                    <p className='text-sm font-medium capitalize' style={{
                      color: movement._id === 'increment' ? 'rgb(22, 163, 74)' : 'rgb(220, 38, 38)'
                    }}>
                      {movement._id === 'increment' ? '↑ Stock In' : '↓ Stock Out'}
                    </p>
                    <p className='text-xs text-gray-600'>{movement.count} transactions</p>
                  </div>
                  <p className='text-2xl font-bold font-space-grotesk' style={{
                    color: movement._id === 'increment' ? 'rgb(22, 163, 74)' : 'rgb(220, 38, 38)'
                  }}>
                    {movement.totalQuantity}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InventoryDashboard